/**
 * Mirror of `@arbitrage-core/notifications-core`'s NotificationEvent
 * shape. Kept in sync manually because the frontend doesn't import
 * from the backend workspace; if the backend type evolves, update
 * here too. Common-types is the natural home long-term.
 */

/**
 * The 48 BASE event types. The implicit `entry_` prefix that used to
 * distinguish opener (BUY) from unwinder (SELL) events is GONE — the
 * backend now stamps an explicit `direction: 'entry'|'unwind'|'system'`
 * field on every event and the `type` is the base name only. Read
 * `evt.direction` (not the type prefix) to know which side of the
 * cycle an event is about — see `getNotificationDirection`.
 */
export type NotificationEventType =
  | 'rule_started'
  | 'rule_completed'
  | 'rule_paused'
  | 'rule_cancelled'
  | 'maker_placed'
  | 'maker_adjusted'
  | 'maker_cancelled'
  | 'maker_partial_fill'
  | 'partial_fill_accumulated'
  | 'maker_filled'
  | 'counter_placed'
  | 'counter_filled'
  | 'crossing_executed'
  | 'crossing_partial'
  | 'crossing_accumulated'
  | 'manual_order_placed'
  | 'manual_order_executed'
  | 'manual_order_partial_fill'
  | 'execution_complete'
  | 'error'
  | 'counter_failed'
  | 'out_of_balance'
  | 'rule_below_minimum'
  | 'dust_remaining'
  | 'market_takeover'
  | 'tna_negative'
  | 'net_contracts_digest'
  | 'emergency_stop'
  | 'emergency_resume'
  | 'recovery_filled'
  | 'recovery_restored'
  | 'counter_skipped'
  | 'max_volume_reached'
  | 'maker_yielded_to_counter'
  | 'platform_disconnect'
  | 'platform_reconnect'
  | 'book_stale'
  | 'book_fresh'
  | 'maker_quiet_cancel'
  | 'constraint_violated'
  | 'over_buy'
  | 'over_sell'
  | 'leg_imbalance'
  | 'engine_live_task_panic'
  | 'order_rejected_rate_limited'
  | 'order_rate_limit_waiting'
  | 'counter_stuck'
  | 'counter_market_hedge'
  | 'counter_maker_walk_step'
  | 'entry_counter_maker_walk_step'
  | 'hedge_deferred'
  | 'hedge_unhedged_panic'
  | 'belief_position_drift'
  | 'pair_missing_token_ids'
  | 'unresolved_fill_naked_leg';

/**
 * Curated allowlist of "social-style" notification types.
 *
 * These are NOT per-operation trading-lifecycle events (fills, orders,
 * amends, rule bookends) — they are account / portfolio-level alerts the
 * operator wants surfaced like a social-network notification: a sidebar
 * bell with an unread badge + dropdown, NOT a row in the dense operations
 * feed at `/predictions/notifications`.
 *
 * Membership is the single source of truth for the split:
 *   - The operations feed EXCLUDES every type in this set (REST query +
 *     realtime prepend + type-filter dropdown) so they "move out entirely".
 *   - The sidebar bell (`useSocialNotifications` / `NotificationBell`)
 *     shows ONLY the types in this set.
 *
 * To promote a future event type into the bell, add it here — both
 * surfaces react automatically. Starts with `tna_negative`.
 */
export const SOCIAL_NOTIFICATION_TYPES: readonly NotificationEventType[] = [
  'tna_negative',
];

const SOCIAL_NOTIFICATION_TYPE_SET: ReadonlySet<string> = new Set(
  SOCIAL_NOTIFICATION_TYPES,
);

/** True when an event type belongs on the sidebar bell, not the ops feed. */
export function isSocialNotification(
  type: NotificationEventType | string,
): boolean {
  return SOCIAL_NOTIFICATION_TYPE_SET.has(type);
}

export type NotificationSeverity = 'info' | 'warning' | 'error';
export type NotificationSource = 'automation' | 'manual' | 'system';
export type NotificationPlatform = 'kalshi' | 'polymarket';

export interface NotificationEvent<TPayload = Record<string, unknown>> {
  id: string;
  type: NotificationEventType;
  /**
   * Which side of the arbitrage cycle this event belongs to. Stamped
   * explicitly by the backend dispatcher (replaced the implicit
   * `entry_` type-name prefix). Optional only so a not-yet-migrated
   * in-flight doc still type-checks; `getNotificationDirection` falls
   * back to a system/unwind default when it's absent.
   */
  direction?: NotificationDirection;
  severity: NotificationSeverity;
  source: NotificationSource;
  occurredAt: string;
  /**
   * Per-rule monotonic sequence stamped by the backend dispatcher.
   * Present iff the event has a `ruleId`. Used as the secondary
   * sort key behind `occurredAt` so two events for the same rule
   * fired in the same JS tick on the producer (canonical case:
   * `counter_filled` → `execution_complete`) render in source order
   * regardless of WS arrival order. System / manual events have no
   * `seq` because they have no `ruleId` and cross-rule ordering is
   * not a logical invariant.
   */
  seq?: number | null;
  ruleId?: string | null;
  pairId?: string | null;
  pairName?: string | null;
  platform?: NotificationPlatform | string | null;
  kalshiTicker?: string | null;
  polyConditionId?: string | null;
  payload: TPayload;
}

/**
 * Direction axis: which side of the arbitrage cycle a notification is
 * about. Mirrors the same enum in `notification-display.ts`. Kept in
 * the type module so filter state is fully typed without importing
 * from the components folder.
 */
export type NotificationDirection = 'entry' | 'unwind' | 'system';

/** Filter state held in URL search params + the filters bar. */
export interface NotificationsFilter {
  from?: string;
  to?: string;
  ruleId?: string;
  pairId?: string;
  types?: NotificationEventType[];
  /**
   * Types to hide from the feed (Mongo `$nin`). Used to hide high-volume /
   * low-value events (maker amends) by default without enumerating every
   * other type. ANDed with `types` on the backend.
   */
  excludeTypes?: NotificationEventType[];
  direction?: NotificationDirection | 'all';
  platform?: NotificationPlatform | 'all';
  source?: NotificationSource | 'all';
  severity?: NotificationSeverity | 'all';
}

export interface NotificationsListResponse {
  items: NotificationEvent[];
  nextCursor: string | null;
  count: number;
}
